import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const Telegram = ({ api, chats, reload }) => {
  const [link, setLink] = useState(null);
  const get = async () => { try { const { data } = await axios.post(`${api}/link-code`); setLink(data); } catch { toast.error("Could not create a link code"); } };
  const unlink = async (id) => { try { await axios.delete(`${api}/chats/${id}`); reload(); } catch { toast.error("Could not remove the chat"); } };
  return (
    <div className="space-y-3" data-testid="desk-telegram">
      <div className="text-base font-semibold">Alert chats</div>
      {chats.length === 0 && <p className="text-xs text-[color:var(--mute)]" data-testid="desk-chats-empty">No chats linked yet.</p>}
      {chats.map((c) => (
        <div key={c.id} className="flex justify-between items-center text-sm" data-testid={`desk-chat-${c.id}`}>
          <span>{c.title || "Chat"} <span className="desk-mono text-[11px] text-[color:var(--mute)]">{c.id}</span></span>
          <button className="desk-btn danger !py-1" onClick={() => unlink(c.id)} data-testid={`desk-chat-unlink-${c.id}`}>Remove</button>
        </div>
      ))}
      <button className="desk-btn" onClick={get} data-testid="desk-link-chat">Link a Telegram chat</button>
      {link && (
        <div className="text-xs space-y-2 p-3 rounded-lg bg-[#0f100d]" data-testid="desk-link-info">
          <div>Private chat: <a className="underline text-[color:var(--gold)]" href={link.private_link} target="_blank" rel="noreferrer" data-testid="desk-link-private">open the bot and press Start</a></div>
          <div>Group: add the bot to the group, then send <span className="desk-mono select-all" data-testid="desk-link-group">{link.group_command}</span></div>
          <div className="text-[color:var(--mute)]">Valid for 10 minutes.</div>
        </div>
      )}
    </div>
  );
};

const Mutes = ({ api, types, mutes, reload }) => {
  const set = async (type, hours) => { try { await axios.put(`${api}/mute`, { type, hours }); reload(); } catch { toast.error("Could not update the mute"); } };
  return (
    <div className="space-y-2" data-testid="desk-mutes">
      <div className="text-base font-semibold">Alerts · mute / snooze</div>
      {Object.entries(types).map(([k, label]) => {
        const until = mutes[k] && new Date(mutes[k]) > new Date() ? new Date(mutes[k]) : null;
        return (
          <div key={k} className="flex justify-between items-center gap-2 text-sm" data-testid={`desk-mute-row-${k}`}>
            <span>{label}{until && <span className="text-[11px] text-[color:var(--amber)]"> · muted until {until.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}</span>}</span>
            {until ? <button className="desk-btn !py-1" onClick={() => set(k, 0)} data-testid={`desk-unmute-${k}`}>Unmute</button> :
              <span className="flex gap-1"><button className="desk-btn !py-1" onClick={() => set(k, 1)} data-testid={`desk-mute-1h-${k}`}>1h</button>
                <button className="desk-btn !py-1" onClick={() => set(k, 24)} data-testid={`desk-mute-24h-${k}`}>24h</button></span>}
          </div>
        );
      })}
    </div>
  );
};

const Thresholds = ({ api, s, reload }) => {
  const [v, setV] = useState({ spike_usd: s.spike_usd, atr_mult: s.atr_mult });
  const save = async () => {
    try { await axios.put(`${api}/thresholds`, { spike_usd: Number(v.spike_usd), atr_mult: Number(v.atr_mult) }); toast.success("Thresholds saved"); reload(); } catch { toast.error("Enter valid numbers"); }
  };
  return (
    <div className="space-y-2" data-testid="desk-thresholds">
      <div className="text-base font-semibold">Volatility thresholds</div>
      <label className="text-xs text-[color:var(--mute)]">$ move in 5 min<input type="number" value={v.spike_usd} onChange={(e) => setV({ ...v, spike_usd: e.target.value })} data-testid="desk-spike-usd" /></label>
      <label className="text-xs text-[color:var(--mute)]">ATR multiple of 20-day avg<input type="number" step="0.1" value={v.atr_mult} onChange={(e) => setV({ ...v, atr_mult: e.target.value })} data-testid="desk-atr-mult" /></label>
      <button className="desk-btn" onClick={save} data-testid="desk-thresholds-save">Save thresholds</button>
    </div>
  );
};

const Tools = ({ api, reload }) => {
  const post = async (path, body, msg) => { try { await axios.post(`${api}/${path}`, body); toast.success(msg); reload(); } catch { toast.error("Action failed"); } };
  const rotate = async () => {
    if (!window.confirm("Generate a new secret link? The current link stops working immediately.")) return;
    try { const { data } = await axios.post(`${api}/rotate`); localStorage.setItem("gtax_desk_token", data.token); window.location.replace("/desk"); } catch { toast.error("Could not rotate the link"); }
  };
  const [log, setLog] = useState(null);
  return (
    <div className="space-y-3" data-testid="desk-tools">
      <div className="text-base font-semibold">Tools</div>
      <div className="flex flex-wrap gap-2">
        <button className="desk-btn primary" onClick={() => post("brief", {}, "Morning brief is being sent")} data-testid="desk-send-brief">Send brief now</button>
        <button className="desk-btn" onClick={() => post("refresh", {}, "Sources refreshed")} data-testid="desk-refresh">Refresh sources</button>
        <button className="desk-btn" onClick={() => post("simulate", { kind: "blackout" }, "Simulated event in 10 min")} data-testid="desk-sim-blackout">Simulate blackout</button>
        <button className="desk-btn" onClick={() => post("simulate", { kind: "headline" }, "Simulated strength-5 headline")} data-testid="desk-sim-headline">Simulate strength-5</button>
        <button className="desk-btn" onClick={() => post("simulate", { kind: "clear" }, "Simulations cleared")} data-testid="desk-sim-clear">Clear simulations</button>
        <button className="desk-btn" onClick={async () => setLog((await axios.get(`${api}/alerts`)).data)} data-testid="desk-alert-log-btn">Alert log</button>
        <button className="desk-btn danger" onClick={rotate} data-testid="desk-rotate">Rotate link</button>
      </div>
      {log && (
        <ul className="max-h-72 overflow-y-auto text-xs space-y-2" data-testid="desk-alert-log">
          {log.length === 0 && <li className="text-[color:var(--mute)]">No alerts yet.</li>}
          {log.map((a) => <li key={a.key} className="border-b border-[color:var(--line)] pb-2"><span className="desk-mono text-[color:var(--mute)]">{new Date(a.at).toLocaleString()} · {a.type}{a.muted ? " · muted" : ""} · sent {a.sent_to?.filter((x) => x.ok).length || 0}</span><div className="whitespace-pre-line">{a.text.replace(/<[^>]+>/g, "").slice(0, 220)}</div></li>)}
        </ul>
      )}
    </div>
  );
};

export const DeskControls = ({ api, state, reload }) => (
  <details className="desk-card desk-details" data-testid="desk-controls">
    <summary data-testid="desk-controls-toggle">Alerts, Telegram & settings <span className="text-[color:var(--gold)]">+</span></summary>
    <Toaster theme="dark" position="top-center" />
    <div className="space-y-6 mt-5">
      <Telegram api={api} chats={state.settings.chats} reload={reload} />
      <Mutes api={api} types={state.alert_types} mutes={state.settings.mutes} reload={reload} />
      <Thresholds api={api} s={state.settings} reload={reload} />
      <Tools api={api} reload={reload} />
    </div>
  </details>
);
